"use client";

import useSWR from "swr";
import ApiClient from "../../backends/ApiClient";
import { useEffect, useMemo, useState } from "react";
import styles from "./SearchPage.module.scss";
import SafeRouter from "../../components/utilities/SafeRouter";
import {
  getQueries,
  isSchoolBlocksApp,
} from "../../utils/SchoolBlocksUtilities";
import { ExpandYourSearchInfoBox } from "./PageSearchResults";
import { ReactiveContentItem } from "../../components/schoolfeed/SchoolFeedContentItem";
import { PageSearchResults } from "./PageSearchResults";
import { FilesAndFoldersResults } from "./FilesAndFoldersResults";
import { UserResults } from "./UserResults";
import { ContentItemResults } from "./ContentItemResults";
import classNames from "classnames";
import useClickOutside from "../../hooks/useClickOutside";
import SearchBox from "./SearchBox";
import useSwipe from "../../hooks/useSwipe";
import { useStores } from "../../stores/useStores";
import {
  ISearchType as AnalyticsSearchType,
  useAnalyticsSafe,
} from "../../context/AnalyticsContext";

type IResultType = {
  data: any;
  meta: {
    total: number;
  };
};

export type ISearchType =
  | "news_items"
  | "event_items"
  | "pages"
  | "staff_members"
  | "google_drive"
  | "files_and_folders";

export type ISearchFilters = {
  "pages.type__in": string[];
  "pages.published": "0" | "1" | undefined;
  "pages.page": number;
  "pages.page_size": number;
  "pages.search"?: string;
  "pages.path__lquery_match"?: string;

  "news_items.is_news": boolean;
  "news_items.expand": string[];
  "news_items.page": number;
  "news_items.page_size": number;
  "news_items.following"?: boolean;

  "event_items.is_event": boolean;
  "event_items.expand": string[];
  "event_items.page": number;
  "event_items.page_size": number;
  "event_items.following"?: boolean;

  "files_and_folders.is_file": boolean;
  "files_and_folders.is_folder": boolean;
  "files_and_folders.expand": string[];
  "files_and_folders.page": number;
  "files_and_folders.page_size": number;

  "google_drive.page": number;
  "google_drive.page_size": number;

  "staff_members.organization__type__in"?: Array<"school" | "district">;
};

export const searchTypesDisplayMap: {
  [key in ISearchType]: string;
} = {
  pages: "Pages",
  news_items: "News",
  event_items: "Events",
  staff_members: "Staff & Teachers",
  google_drive: "Google Drive",
  files_and_folders: "Files & Folders",
};

export function buildResultsHeaderText(
  num: number,
  type: string,
  searchTerm: string,
): string {
  let results = `${num} ${type} Result${num > 1 || num === 0 ? "s" : ""}`;
  if (type === searchTypesDisplayMap.files_and_folders && searchTerm) {
    results += ` found with '${searchTerm}' in their title`;
  } else if (num > 0) {
    results += ` for '${searchTerm}'`;
  }
  return results;
}

export default function SearchPage() {
  const { organizationStore, userStore, interfaceStore } = useStores();
  const isSchoolBlocks = isSchoolBlocksApp();

  const queries = getQueries(interfaceStore.currentFullUrl);

  const [searchTypes, setSearchTypes] = useState<ISearchType[]>([
    isSchoolBlocksApp() ? "pages" : "news_items",
  ]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const swipe = useSwipe({
    handleUpwardsSwipe: () => {
      setFiltersExpanded(false);
    },
    handleDownwardsSwipe: () => {
      setFiltersExpanded(true);
    },
  });
  const ref = useClickOutside(() => {
    if (filtersExpanded) {
      setFiltersExpanded(false);
    }
  });

  let searchTerm = "";
  try {
    searchTerm = queries.q ? decodeURIComponent(queries.q) : "";
  } catch {
    searchTerm = "";
  }

  const [inputValue, setInputValue] = useState<string>(searchTerm);
  const isDistrict = organizationStore.organization.type === "district";
  const everythingLquery = `${organizationStore.organization.path}.*`;
  const canFilterSchools =
    isDistrict &&
    isSchoolBlocks &&
    organizationStore.currentOrganization.school_ids.length > 0;

  const organizationTypes = useMemo(() => {
    if (organizationStore.organization.type === "district") {
      return ["school", "group", "department", "section", "district", "page"];
    } else if (organizationStore.organization.type === "school") {
      return ["group", "section", "page"];
    } else {
      return ["organization"];
    }
  }, []);

  const { track, setSearchContext } = useAnalyticsSafe();

  async function handleSubmit() {
    SafeRouter.setQuery("q", encodeURIComponent(inputValue));
    setFiltersExpanded(false);
  }

  const [searchFilters, setSearchFilters] = useState<ISearchFilters>(() => {
    const f: ISearchFilters = {
      "pages.page": 1,
      "pages.page_size": 12,
      "pages.type__in": organizationTypes,
      "pages.published": "1",
      "pages.path__lquery_match": `${organizationStore.organization.path}.*`,

      "news_items.is_news": true,
      "news_items.expand": ["organization"],
      "news_items.page": 1,
      "news_items.page_size": 12,

      "event_items.is_event": true,
      "event_items.expand": ["organization"],
      "event_items.page": 1,
      "event_items.page_size": 12,

      "files_and_folders.is_file": true,
      "files_and_folders.is_folder": true,
      "files_and_folders.expand": ["organization"],
      "files_and_folders.page": 1,
      "files_and_folders.page_size": 12,

      "google_drive.page": 1,
      "google_drive.page_size": 12,

      "staff_members.organization__type__in": isDistrict
        ? ["school", "district"]
        : undefined,
    };

    return f;
  });

  const cacheKey = JSON.stringify({
    query: searchTerm,
    limit_results: searchTypes,
    ...searchFilters,
  });

  const {
    data: results,
    isValidating,
    mutate,
  } = useSWR(
    searchTerm ? cacheKey : null,
    async (key) => {
      // eslint-disable-next-line react-hooks/immutability
      const prevKey = results?.prevKey;
      const nextKey = JSON.parse(key);
      const nextQuery = { ...searchFilters };

      // limit results to only the types relevant to what filters have changed
      let limitResults: ISearchType[] = [];
      if (prevKey && prevKey.query === nextKey.query) {
        // not initial load and query hasn't changed
        for (const k in { ...prevKey, ...nextKey }) {
          const match =
            /^(pages|staff_members|files_and_folders|news_items|event_items|google_drive).*/.exec(
              k,
            );
          if (
            JSON.stringify(nextKey[k]) !== JSON.stringify(prevKey[k]) &&
            match &&
            searchTypes.includes(match[1] as ISearchType)
          ) {
            limitResults.push(match[1] as ISearchType);
            if (k !== `${match[1]}.page`) {
              nextQuery[`${match[1]}.page`] = 1; // reset the page of this result type since filters have changed
            }
          }
        }
      } else {
        // query has changed or initial load, fetch all results
        limitResults = searchTypes;
        searchTypes.forEach((t) => {
          // when query changes, make sure we reset the page
          nextQuery[`${t}.page`] = 1;
        });
      }

      if (
        prevKey &&
        prevKey.limit_results.length < nextKey.limit_results.length
      ) {
        // if we've added a result type, only need to search the new result type
        limitResults = nextKey.limit_results.filter(
          (x) => !prevKey.limit_results.includes(x),
        );
      }

      let parsed: {
        pages?: IResultType;
        news_items?: IResultType;
        event_items?: IResultType;
        files_and_folders?: IResultType;
        staff_members?: IResultType;
        google_drive?: IResultType;
      } = {};
      if (limitResults.length > 0) {
        // we may not need to do anything, for example if a result type has only been removed
        const client = await ApiClient();
        // the spec types nearly every query param here as a plain string (and
        // documents this endpoint as returning no body), but the backend has
        // always accepted these richer types (numbers, arrays, booleans) and
        // returned a JSON body keyed by result type (SBSB-2912)
        parsed = await (client.GET("/organizations/{organization_pk}/search/", {
          params: {
            path: { organization_pk: organizationStore.organization.id },
            query: {
              query: searchTerm,
              limit_results: limitResults,
              ...nextQuery,
            } as never,
          },
        }) as any);
      }
      return {
        pages: parsed.pages || results?.pages,
        news_items: parsed.news_items
          ? {
              ...parsed.news_items,
              data: parsed.news_items.data.map(
                (r) => new ReactiveContentItem(r),
              ),
            }
          : results?.news_items,
        event_items: parsed.event_items
          ? {
              ...parsed.event_items,
              data: parsed.event_items.data.map(
                (r) => new ReactiveContentItem(r),
              ),
            }
          : results?.event_items,
        files_and_folders: parsed.files_and_folders
          ? {
              ...parsed.files_and_folders,
              data: parsed.files_and_folders.data.map(
                (r) => new ReactiveContentItem(r),
              ),
            }
          : results?.files_and_folders,
        staff_members: parsed.staff_members || results?.staff_members,
        google_drive: parsed.google_drive || results?.google_drive,
        prevKey: nextKey,
      };
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      keepPreviousData: true,
    },
  );

  const hasNoResults =
    results &&
    Array.from(Object.keys(results))
      .filter((k) => k !== "prevKey")
      .every((k) => {
        return results[k] === undefined || results[k]?.meta?.total === 0;
      });

  // Set search context for analytics auto-enrichment
  useEffect(() => {
    if (searchTerm) {
      const searchPages: Partial<Record<AnalyticsSearchType, number>> = {
        pages: searchFilters["pages.page"],
        news_items: searchFilters["news_items.page"],
        event_items: searchFilters["event_items.page"],
        files_and_folders: searchFilters["files_and_folders.page"],
      };
      setSearchContext({
        searchTerm,
        searchTypes: searchTypes as AnalyticsSearchType[],
        pages: searchPages,
      });
    }
    return () => setSearchContext(null);
  }, [
    searchTerm,
    searchTypes,
    searchFilters["pages.page"],
    searchFilters["news_items.page"],
    searchFilters["event_items.page"],
    searchFilters["files_and_folders.page"],
    setSearchContext,
  ]);

  useEffect(() => {
    if (hasNoResults && searchTerm && !isValidating) {
      track("search_zero_results", {
        search_term: searchTerm,
        search_types_enabled: searchTypes,
      });
    }
  }, [hasNoResults, searchTerm, isValidating, searchTypes, track]);

  const filtersClassName = classNames({
    [styles.filtersContainer]: true,
    [styles.filtersContainerExpanded]: filtersExpanded,
  });
  const filtersListClassNames = classNames({
    [styles.filtersList]: true,
    [styles.filtersListExpanded]: filtersExpanded,
  });
  const filterListSearchClassNames = classNames({
    [styles.filtersListSearch]: true,
    [styles.filtersListSearchExpanded]: filtersExpanded,
    [styles.filtersListSearchHasNoResults]: !filtersExpanded,
  });

  return searchTerm ? (
    <main id="sb-main-content" className={styles.container}>
      <h1 className={styles.searchHeading}>
        Search {organizationStore.organization.title}
      </h1>
      <div className={filtersClassName} ref={ref}>
        <div className={styles.mobileSearchFilterGrabBar} ref={swipe}>
          <div className={styles.filtersBody}>
            <div className={filterListSearchClassNames}>
              <SearchBox
                searchTerm={searchTerm}
                isValidating={isValidating}
                inputValue={inputValue}
                setInputValue={setInputValue}
                handleSubmit={handleSubmit}
                searchTypes={searchTypes}
              />
            </div>

            <button
              type="button"
              className={styles.filterToggle}
              aria-expanded={filtersExpanded}
              aria-controls="search-result-filters"
              onClick={() => setFiltersExpanded((expanded) => !expanded)}
            >
              {filtersExpanded ? "Hide filters" : "Show filters"}
            </button>
            <ul
              id="search-result-filters"
              className={filtersListClassNames}
              aria-label={"Filter results"}
            >
              {canFilterSchools && (
                <li>
                  <div>
                    <label>
                      <input
                        checked={
                          searchFilters["pages.path__lquery_match"] ===
                          everythingLquery
                        }
                        type={"checkbox"}
                        onChange={() => {
                          const prev =
                            searchFilters["pages.path__lquery_match"];
                          const districtOnlyLquery = `${
                            organizationStore.organization.path
                          }.!${organizationStore.currentOrganization.school_ids
                            .map((id) => id.replaceAll("-", ""))
                            .join("|")}{1}.*`;

                          const newLqueryValue =
                            prev === everythingLquery
                              ? districtOnlyLquery
                              : everythingLquery;
                          setSearchFilters((v) => {
                            const r = {
                              ...v,
                              "pages.path__lquery_match": newLqueryValue,
                              "files_and_folders.organization__path__lquery_match":
                                newLqueryValue,
                              "news_items.organization__path__lquery_match":
                                newLqueryValue,
                              "event_items.organization__path__lquery_match":
                                newLqueryValue,
                            };
                            if (isDistrict) {
                              r["staff_members.organization__type__in"] =
                                prev === everythingLquery
                                  ? ["district"]
                                  : ["district", "school"];
                            }
                            return r;
                          });
                        }}
                      />
                      Include Schools
                    </label>
                  </div>
                </li>
              )}
              <li>
                <div>
                  <label>
                    <input
                      checked={searchTypes.includes("pages")}
                      type={"checkbox"}
                      onChange={() => {
                        if (searchTypes.includes("pages")) {
                          setSearchTypes((v) => v.filter((t) => t !== "pages"));
                        } else {
                          setSearchTypes((v) => ["pages", ...v]);
                        }
                      }}
                    />
                    Pages
                  </label>
                </div>
                {isSchoolBlocks && (
                  <div>
                    <label>
                      <input
                        checked={searchTypes.includes("staff_members")}
                        type={"checkbox"}
                        onChange={() => {
                          if (searchTypes.includes("staff_members")) {
                            setSearchTypes((v) =>
                              v.filter((t) => t !== "staff_members"),
                            );
                          } else {
                            setSearchTypes((v) => ["staff_members", ...v]);
                          }
                        }}
                      />
                      {searchTypesDisplayMap.staff_members}
                    </label>
                  </div>
                )}
                <div>
                  <label>
                    <input
                      checked={searchTypes.includes("news_items")}
                      type={"checkbox"}
                      onChange={() => {
                        if (searchTypes.includes("news_items")) {
                          setSearchTypes((v) =>
                            v.filter((t) => t !== "news_items"),
                          );
                        } else {
                          setSearchTypes((v) => ["news_items", ...v]);
                        }
                      }}
                    />
                    {searchTypesDisplayMap.news_items}
                  </label>
                </div>
                <div>
                  <label>
                    <input
                      checked={searchTypes.includes("event_items")}
                      type={"checkbox"}
                      onChange={() => {
                        if (searchTypes.includes("event_items")) {
                          setSearchTypes((v) =>
                            v.filter((t) => t !== "event_items"),
                          );
                        } else {
                          setSearchTypes((v) => ["event_items", ...v]);
                        }
                      }}
                    />
                    {searchTypesDisplayMap.event_items}
                  </label>
                </div>
                <div>
                  <label>
                    <input
                      checked={searchTypes.includes("files_and_folders")}
                      type={"checkbox"}
                      onChange={() => {
                        if (searchTypes.includes("files_and_folders")) {
                          setSearchTypes((v) =>
                            v.filter((t) => t !== "files_and_folders"),
                          );
                        } else {
                          setSearchTypes((v) => ["files_and_folders", ...v]);
                        }
                      }}
                    />
                    {searchTypesDisplayMap.files_and_folders}
                  </label>
                </div>
              </li>
              {userStore.editor && isSchoolBlocks && (
                <li>
                  <h3>Page Status</h3>
                  <div>
                    <label>
                      <input
                        type={"checkbox"}
                        checked={searchFilters["pages.published"] === "1"}
                        onChange={() => {
                          setSearchFilters((v) => {
                            const prev = v["pages.published"];
                            return {
                              ...v,
                              "pages.published": prev === "1" ? undefined : "1",
                            };
                          });
                        }}
                      />
                      Published
                    </label>
                  </div>
                  <div>
                    <label>
                      <input
                        type={"checkbox"}
                        checked={searchFilters["pages.published"] === "0"}
                        onChange={() => {
                          setSearchFilters((v) => {
                            const prev = v["pages.published"];
                            return {
                              ...v,
                              "pages.published": prev === "0" ? undefined : "0",
                            };
                          });
                        }}
                      />
                      Draft
                    </label>
                  </div>
                </li>
              )}
            </ul>
          </div>
          <hr className={styles.collapseBar} />
        </div>
      </div>
      {hasNoResults ? (
        <div className={styles.initialContainer}>
          <div>
            <h2>
              No results containing{" "}
              <span style={{ fontWeight: 700 }}>{searchTerm}</span> were found.
            </h2>
            {organizationStore.organization.json_data?.settings?.identity
              ?.provider === "google" &&
              organizationStore.organization.json_data.settings.identity.google
                .searchDrive &&
              isSchoolBlocks &&
              searchTypes.includes("files_and_folders") && (
                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  <ExpandYourSearchInfoBox
                    onClick={() =>
                      searchTypes.includes("google_drive")
                        ? setSearchTypes((v) =>
                            v.filter((t) => t !== "google_drive"),
                          )
                        : setSearchTypes((v) => ["google_drive", ...v])
                    }
                    buttonText={
                      searchTypes.includes("google_drive")
                        ? "Search site files"
                        : "Search Google Drive"
                    }
                    searchType={"files_and_folders"}
                  />
                </div>
              )}
            <div className={styles.onPageSearchBox}>
              <SearchBox
                searchTerm={searchTerm}
                isValidating={isValidating}
                inputValue={inputValue}
                setInputValue={setInputValue}
                handleSubmit={handleSubmit}
                searchTypes={searchTypes}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.blocksContainer}>
          {searchTypes.map((searchType) => {
            switch (searchType) {
              case "pages":
                return (
                  <PageSearchResults
                    searchTypes={searchTypes}
                    results={results?.pages}
                    filters={searchFilters}
                    setFilters={setSearchFilters}
                    isValidating={isValidating}
                    searchTerm={searchTerm}
                    key={searchType}
                  />
                );
              case "news_items":
                return (
                  <ContentItemResults
                    contentType={"news_items"}
                    searchTypes={searchTypes}
                    filters={searchFilters}
                    setFilters={setSearchFilters}
                    results={results}
                    isValidating={isValidating}
                    cacheKey={cacheKey}
                    mutate={mutate}
                    searchTerm={searchTerm}
                    key={searchType}
                  />
                );
              case "event_items":
                return (
                  <ContentItemResults
                    contentType={"event_items"}
                    searchTypes={searchTypes}
                    filters={searchFilters}
                    setFilters={setSearchFilters}
                    results={results}
                    isValidating={isValidating}
                    cacheKey={cacheKey}
                    mutate={mutate}
                    searchTerm={searchTerm}
                    key={searchType}
                  />
                );
              case "staff_members":
                return (
                  <UserResults
                    searchTypes={searchTypes}
                    results={results?.staff_members}
                    isValidating={isValidating}
                    searchTerm={searchTerm}
                    key={searchType}
                  />
                );
              case "files_and_folders":
                return (
                  <FilesAndFoldersResults
                    filters={searchFilters}
                    setFilters={setSearchFilters}
                    searchTypes={searchTypes}
                    setSearchTypes={setSearchTypes}
                    results={results}
                    isValidating={isValidating}
                    searchTerm={searchTerm}
                    key={searchType}
                  />
                );
            }
          })}
        </div>
      )}
    </main>
  ) : (
    <main id="sb-main-content" className={styles.initialContainer}>
      <div>
        <h1>Search {organizationStore.organization.title}</h1>
        <SearchBox
          searchTerm={searchTerm}
          isValidating={isValidating}
          inputValue={inputValue}
          setInputValue={setInputValue}
          handleSubmit={handleSubmit}
          searchTypes={searchTypes}
        />
      </div>
    </main>
  );
}
